import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Hook for managing saved property listings
 * Provides functions to save, retrieve, update, and delete listings
 */
export function useSavedListings() {
    const [savedListings, setSavedListings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Fetch all saved listings for the current user
     */
    const fetchSavedListings = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Not authenticated');
            }

            const response = await axios.get(`${API_URL}/api/saved-listings`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setSavedListings(response.data.listings);
            } else {
                throw new Error(response.data.error || 'Failed to fetch listings');
            }
        } catch (err) {
            console.error('Error fetching saved listings:', err);
            setError(err.message);
            setSavedListings([]);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Save a new listing
     */
    const saveListing = useCallback(async (listingUrl, propertyData, analysis, notes = '', tags = []) => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Not authenticated');
            }

            const response = await axios.post(
                `${API_URL}/api/saved-listings`,
                {
                    listingUrl,
                    propertyData,
                    analysis,
                    notes,
                    tags
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                // Add to local state
                setSavedListings(prev => [response.data.listing, ...prev]);
                return response.data.listing;
            } else {
                throw new Error(response.data.error || 'Failed to save listing');
            }
        } catch (err) {
            console.error('Error saving listing:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Update a saved listing
     */
    const updateListing = useCallback(async (listingId, updates) => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Not authenticated');
            }

            const response = await axios.put(
                `${API_URL}/api/saved-listings/${listingId}`,
                updates,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                // Update local state
                setSavedListings(prev =>
                    prev.map(listing =>
                        listing.id === listingId ? response.data.listing : listing
                    )
                );
                return response.data.listing;
            } else {
                throw new Error(response.data.error || 'Failed to update listing');
            }
        } catch (err) {
            console.error('Error updating listing:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Delete a saved listing
     */
    const deleteListing = useCallback(async (listingId) => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Not authenticated');
            }

            const response = await axios.delete(
                `${API_URL}/api/saved-listings/${listingId}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                // Remove from local state
                setSavedListings(prev => prev.filter(listing => listing.id !== listingId));
                return true;
            } else {
                throw new Error(response.data.error || 'Failed to delete listing');
            }
        } catch (err) {
            console.error('Error deleting listing:', err);
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Check if a URL is already saved
     */
    const isListingSaved = useCallback((url) => {
        return savedListings.some(listing => listing.listingUrl === url);
    }, [savedListings]);

    // Auto-fetch on mount
    useEffect(() => {
        fetchSavedListings();
    }, [fetchSavedListings]);

    return {
        savedListings,
        loading,
        error,
        saveListing,
        updateListing,
        deleteListing,
        isListingSaved,
        refetch: fetchSavedListings
    };
}
